import React from 'react';
import { useTranslation } from 'react-i18next';
import slugify from 'slugify';
import styles from './styles/ChangePaymentMethod.module.scss';

import { OmaLummeApp } from '../../omaLummeContext';

import BackButton from '../Common/BackButton';
import Box from '../Common/Box';
import Button from '../Common/Button';
import ManageContractsRadio from '../Contracts/ManageContracts/ManageContractsRadioCheckbox';

import { eBillBanks } from '../Contracts/ManageContracts/sections/Billing';

// Actions.
import { putPaymentType } from '../../actions';
// Types.
import { ContractBasicData } from '../../types/contract';
// Utils.
import { validateEmail } from '../../utils/contact';
import { generateAddress } from '../../utils/address';
import { bankNameToId, paymentTypeToNumber } from '../../utils/contractManagement';

type ChangePaymentMethodType = {
  contract: ContractBasicData;
  currentPaymentType: number;
  setChangingPaymentMethod: (value: React.SetStateAction<boolean>) => void;
  setPaymentMethodUpdated: (value: React.SetStateAction<boolean>) => void;
};

const ChangePaymentMethod: React.FunctionComponent<ChangePaymentMethodType> = (props: ChangePaymentMethodType) => {
  const { t } = useTranslation();
  const { users } = OmaLummeApp.useState();
  const { activeUser } = users;
  const [paymentType, setPaymentType] = React.useState<string | undefined>(undefined);
  const [paymentTypeEbillBank, setPaymentTypeEbillBank] = React.useState<string | undefined>(undefined);
  const [paymentTypeNewEmail, setPaymentTypeNewEmail] = React.useState<string | undefined>(undefined);
  const [paymentTypeNewEmailValid, setPaymentTypeNewEmailValid] = React.useState<boolean | undefined>(undefined);
  const [formValid, setFormValid] = React.useState<boolean>(false);

  const { contract, currentPaymentType, setChangingPaymentMethod, setPaymentMethodUpdated } = props;

  const emailExists = activeUser.userEmail && activeUser.userEmail.length > 0;

  const id = slugify(generateAddress(contract.meteringpointAddress, false) + Math.random() * 100).toLowerCase();

  React.useEffect(() => {
    const validateForm = () => {
      if (contract && paymentType === 'eBill' && paymentTypeEbillBank) {
        return true;
      }
      if (contract && paymentType === 'emailMyAddress') {
        return true;
      }
      if (contract && paymentType === 'emailNewAddress' && paymentTypeNewEmail && paymentTypeNewEmailValid) {
        return true;
      }
      return false;
    };

    if (validateForm()) {
      setFormValid(true);
    } else {
      setFormValid(false);
    }
  }, [contract, paymentType, paymentTypeEbillBank, paymentTypeNewEmail, paymentTypeNewEmailValid]);

  const submitForm = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (paymentType && contract) {
      const paymentTypeNo = paymentTypeToNumber(paymentType);
      let bankId: number | undefined = undefined;
      let email: string | undefined = undefined;
      if (paymentType === 'eBill' && paymentTypeEbillBank) {
        bankId = bankNameToId(paymentTypeEbillBank);
      }
      if (paymentType === 'emailMyAddress') {
        email = activeUser.userEmail;
      }
      if (paymentType === 'emailNewAddress') {
        email = paymentTypeNewEmail;
      }
      const paymentTypeRequest = await putPaymentType(activeUser.userId, parseInt(contract.ledgerNo), paymentTypeNo, bankId, email);
      if (paymentTypeRequest.status === 200) {
        setPaymentMethodUpdated(true);
        setChangingPaymentMethod(false);
      }
    }
  };

  return (
    <>
      <Box>
        <BackButton
          onClick={(e) => {
            e.preventDefault();
            setChangingPaymentMethod(false);
          }}
        />
        <h2>{t(`billing:changePayment.title`)}</h2>
        <p>{t(`billing:changePayment.description`)}</p>

        <form className="manage-contracts" onSubmit={submitForm}>
          <section className={`manage-contracts-step ${styles.section}`}>
            <h3>{t('billing:changePayment.selectContractTitle')}</h3>

            <div className="manage-contracts-table old-contract">
              <header className="manage-contracts-table-header">
                <div>{t('manageContracts:manageContracts.TableContract')}</div>
                <div>{t('common:general.contractNumber')}</div>
                <div>{t('billing:changePayment.currentPaymentMethod')}</div>
              </header>

              <div className={`manage-contracts-table-row selected`}>
                <div className="table-cell form-item radio">
                  <input type="radio" name={`oldContract-contract-type`} id={id} checked={true} />
                  <div className="form-item-input"></div>
                  <span className="mobile-label">{t('manageContracts:manageContracts.TableContract')}</span>
                  <label htmlFor={id}>{generateAddress(contract.meteringpointAddress, false)}</label>
                </div>

                <div className="table-cell">
                  <span className="mobile-label">{t('common:general.contractNumber')}</span>
                  {contract.contractCode}
                </div>
                <div className="table-cell">
                  <span className="mobile-label">{t('billing:changePayment.currentPaymentMethod')}</span>
                  {t(`billing:paymentTypes.${currentPaymentType}`)}
                </div>
              </div>
            </div>
          </section>

          <section className={`manage-contracts-step ${styles.section}`}>
            <h3>{t('billing:changePayment.newPaymentMethodTitle')}</h3>

            <div className="form-items">
              <h4>{t('billing:paymentTypes.eBill')}</h4>
              <ManageContractsRadio
                radioID="paymentTypeEbill"
                radioName="paymentType"
                onChangeFunc={(): void => {
                  setPaymentType('eBill');
                }}
                checkedComparison={paymentType === 'eBill'}
                label={`${t('manageContracts:manageContracts.InvoiceEbill')}`}
              />
              {paymentType === 'eBill' && (
                <div className="form-item-sub-content select select-ghost">
                  <select
                    id="address"
                    value={paymentTypeEbillBank}
                    onChange={(event): void => {
                      if (event.target.value !== 'null') {
                        setPaymentTypeEbillBank(event.target.value);
                      }
                    }}
                  >
                    <option value="null">{t('manageContracts:manageContracts.InvoiceEbillSelectBank')}</option>
                    {eBillBanks.map((bank) => (
                      <option key={bank} value={bank}>
                        {bank}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <h4>{t('billing:paymentTypes.email')}</h4>
              {emailExists && (
                <ManageContractsRadio
                  radioID="paymentTypeEmailMyAddress"
                  radioName="paymentType"
                  onChangeFunc={(): void => {
                    setPaymentType('emailMyAddress');
                  }}
                  checkedComparison={paymentType === 'emailMyAddress'}
                  label={`${t('manageContracts:manageContracts.InvoiceEmailMyAddress')} <strong>${activeUser.userEmail}</strong>`}
                />
              )}
              <ManageContractsRadio
                radioID="paymentTypeEmailNewAddress"
                radioName="paymentType"
                onChangeFunc={(): void => {
                  setPaymentType('emailNewAddress');
                  if (
                    document.querySelector('input[id="paymentTypeEmailNewAddress"]:checked') &&
                    (!paymentTypeNewEmail || (paymentTypeNewEmail && !validateEmail(paymentTypeNewEmail)))
                  ) {
                    setPaymentTypeNewEmailValid(false);
                  }
                }}
                checkedComparison={paymentType === 'emailNewAddress'}
                label={`${t(
                  emailExists ? 'manageContracts:manageContracts.InvoiceEmailNewAddress' : 'manageContracts:manageContracts.InvoiceEmailMyAddress',
                )}`}
              />
              {paymentType === 'emailNewAddress' && (
                <div className="form-item-sub-content paymentTypeEmailNewAddressInput">
                  <label htmlFor="paymentTypeEmailNewAddressInput">{t('common:general.email')}</label>
                  <input
                    type="email"
                    id="paymentTypeEmailNewAddressInput"
                    name="email"
                    value={paymentTypeNewEmail}
                    onChange={(e): void => {
                      const emailIsValid = validateEmail(e.target.value);

                      if (emailIsValid) {
                        setPaymentTypeNewEmailValid(true);
                        setPaymentTypeNewEmail(e.target.value);
                      } else {
                        setPaymentTypeNewEmailValid(false);
                      }
                    }}
                    required
                  />
                  {!paymentTypeNewEmailValid && <strong>{t('common:contactform.email')}</strong>}
                </div>
              )}
            </div>
          </section>
          <footer className={styles.footer}>
            <Button
              type="submit"
              disabled={!formValid}
              onClick={(e): void => {
                if (!formValid) {
                  e.preventDefault();
                }
              }}
            >
              {t('billing:changePayment.submit')}
            </Button>
          </footer>
        </form>
      </Box>
    </>
  );
};

export default ChangePaymentMethod;
